import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { BlockTextComponent } from '../../../common/block-text/block-text.component';
import { NotificationComponent } from '../../../common/notification/notification.component';
import { BaseComponent } from '../../../common/BaseComponent';
import { PinComponent } from '../../../common/pin/pin.component';
import { Helper } from '../../../../../utils/helper';
import { YoutubeVideoYoutubeUrlInitModel, YoutubeVideoYoutubeUrlInitVariables, YoutubeVideoYoutubeUrlModel, YoutubeVideoYoutubeUrlVariables } from '../../../../../models/google-template/youtube-video-youtube-url.interface';
import { YoutubeHelper } from '../../../../../utils/youtube-helper';
import { VideoFormat, YoutubeFormatHelper } from '../../../../../utils/youtube-format-helper';

@Component({
    selector: 'app-youtube-video-youtube-url',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        BlockTextComponent,
        NotificationComponent,
        PinComponent,
    ],
    templateUrl: './youtube-video-youtube-url.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './youtube-video-youtube-url.component.scss'
})
export class YoutubeVideoYoutubeUrlComponent extends BaseComponent implements OnInit {
  //@ input
  @Input() block: YoutubeVideoYoutubeUrlModel = new YoutubeVideoYoutubeUrlInitModel();
  @Input() isPreview = false;
  //@ output
  @Output() blockData = new EventEmitter<any>(); // Event emitter to send data to the parent
  @Output() removeBlockEvent = new EventEmitter<string | null>(); // New EventEmitter for remove

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new YoutubeVideoYoutubeUrlInitVariables()
    }
  }

  //@ placeholders
  videoPlaceholder = 'Enter YouTube video link';

  /** CSS aspect-ratio per format, so the thumbnail is the shape its badge claims. */
  private static readonly ROW_ASPECT: Record<VideoFormat, string> = {
    '9:16': '9 / 16',
    '16:9': '16 / 9',
    '1:1': '1 / 1',
  };

  // Memoised by URL: the template asks on every change-detection pass.
  private readonly videoIds = new Map<string, string>();

  /** True once the field holds a link we can read a video id out of. */
  hasVideo(): boolean {
    return !!this.videoId();
  }

  /**
   * Thumbnail at the video's own shape. Empty while the format is
   * still being read and when the link resolves to nothing, so the block never
   * paints YouTube's grey placeholder.
   */
  thumbnail(): string {
    const videoId = this.videoId();
    return videoId ? YoutubeFormatHelper.thumbnailFor(videoId) : '';
  }

  /** The measured format, or '' while it is still being read. */
  formatLabel(): string {
    return this.reading()?.format ?? '';
  }

  /**
   * Set once the link has been read and is not one of the three allowed
   * formats, or resolves to no video at all. A warning, not a block.
   */
  formatWarning(): string {
    const reading = this.reading();
    if (!reading || reading.format) {
      return '';
    }
    return reading.missing
      ? 'No video found — check the link is right and the video is public'
      : `${reading.width}x${reading.height} — not 9:16, 16:9 or 1:1`;
  }

  /** Shape for the thumbnail; falls back to 16:9 until the format is known. */
  rowAspectRatio(): string {
    const format = this.reading()?.format;
    return format
      ? YoutubeVideoYoutubeUrlComponent.ROW_ASPECT[format]
      : YoutubeVideoYoutubeUrlComponent.ROW_ASPECT['16:9'];
  }

  private reading() {
    const videoId = this.videoId();
    return videoId ? YoutubeFormatHelper.readingFor(videoId) : undefined;
  }

  private videoId(): string {
    const url = this.block.variables?.['YoutubeVideoUrl'] ?? '';
    let cached = this.videoIds.get(url);

    if (cached === undefined) {
      cached = YoutubeHelper.extractVideoId(url) ?? '';
      this.videoIds.set(url, cached);
    }

    return cached;
  }

  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId); // Emit the ID to parent
  }

  onChange(value: any, fieldName: keyof YoutubeVideoYoutubeUrlVariables) {
    if (this.block.variables) {
      this.block.variables[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }

  onContainerClick(event: MouseEvent) {
    // Forward the click event to the child app-pin component
    this.pinComponent.onContainerClick(event);
  }

  handleOnSegmentChange(segment: string, blockName: string) {
    if (this.block.variables) {
      this.block.variables[blockName as keyof YoutubeVideoYoutubeUrlVariables] = segment;
      this.blockData.emit(this.block);
    }
    return;
  }
}
